import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { LanguageContext } from "../../context/LanguageContext";
import no from "../../context/no";
import en from "../../context/en";

// Solo
import soloDust from "../../assets/effects/Solo/1-Dust-2.webp";
import soloSun from "../../assets/effects/Solo/3-Sun-explosion.webp";
import soloWater from "../../assets/effects/Solo/4-1-eye---Water-splash.webp";
import soloHeart from "../../assets/effects/Solo/5-Heart-of-hearts.webp";
import soloRing from "../../assets/effects/Solo/Ring Kari Martinussen M 04-10-2026.webp";
import soloPlain from "../../assets/effects/Solo/Kari Martinussen M 04-10-2026.webp";

// Duo — Collision first (default slide)
import duoCollision from "../../assets/effects/Duo/9-Collision-2.webp";
import duoYingYang from "../../assets/effects/Duo/6-Ying-Yang-2.webp";
import duoHeart from "../../assets/effects/Duo/7-Heart-of-Hearts-2.webp";
import duoWater from "../../assets/effects/Duo/8-Water-Splash-2.webp";
import duoPlain from "../../assets/effects/Duo/10-Plain-2---square-right.webp";
import duoInfinity from "../../assets/effects/Duo/11-Infinity.webp";
import duoTwoHearts from "../../assets/effects/Duo/12-Two-Hearts.webp";
import duoHalves from "../../assets/effects/Duo/13-Two-halves---Plain.webp";

// Trio
import trioWater from "../../assets/effects/Trio/14-Water-Splash-3.webp";
import trioRings from "../../assets/effects/Trio/15-Rings-3-stacked.webp";
import trioYingYang from "../../assets/effects/Trio/16-Ying-Yang-3.webp";
import trioCollision from "../../assets/effects/Trio/17-Collision-3.webp";
import trioPlain from "../../assets/effects/Trio/18-Plain-3-eyes---Square.webp";

// Quad
import quadDust from "../../assets/effects/Quad/19-Dust-4-eyes.webp";
import quadRings from "../../assets/effects/Quad/20-Rings-4-square.webp";
import quadPlain from "../../assets/effects/Quad/21-Plain-4-eyes---Square.webp";

// Quintuple
import quintWater from "../../assets/effects/Quintuple/22-Water-Splash-5.webp";
import quintCollision from "../../assets/effects/Quintuple/23-Collision-5-Rosetta.webp";
import quintRings from "../../assets/effects/Quintuple/25-Rings-5-stacked.webp";

const EFFECT_SETS = {
  solo: [
    { src: soloDust, name: "Dust" },
    { src: soloSun, name: "Sun Explosion" },
    { src: soloWater, name: "Water Splash" },
    { src: soloHeart, name: "Heart of Hearts" },
    { src: soloRing, name: "Ring" },
    { src: soloPlain, name: "Plain" },
  ],
  duo: [
    { src: duoCollision, name: "Collision" },
    { src: duoYingYang, name: "Ying Yang" },
    { src: duoHeart, name: "Heart of Hearts" },
    { src: duoWater, name: "Water Splash" },
    { src: duoPlain, name: "Plain" },
    { src: duoInfinity, name: "Infinity" },
    { src: duoTwoHearts, name: "Two Hearts" },
    { src: duoHalves, name: "Two Halves" },
  ],
  trio: [
    { src: trioWater, name: "Water Splash" },
    { src: trioRings, name: "Rings" },
    { src: trioYingYang, name: "Ying Yang" },
    { src: trioCollision, name: "Collision" },
    { src: trioPlain, name: "Plain" },
  ],
  quad: [
    { src: quadDust, name: "Dust" },
    { src: quadRings, name: "Rings" },
    { src: quadPlain, name: "Plain" },
  ],
  quintuple: [
    { src: quintWater, name: "Water Splash" },
    { src: quintCollision, name: "Collision" },
    { src: quintRings, name: "Rings" },
  ],
};

const TAB_IDS = ["solo", "duo", "trio", "quad", "quintuple"];

function EffectsSection() {
  const { language } = useContext(LanguageContext);
  const t = language === "no" ? no.home.effects : en.home.effects;

  const [activeTab, setActiveTab] = useState("duo");
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollerRef = useRef(null);

  const effects = EFFECT_SETS[activeTab];

  const scrollToIndex = useCallback((index) => {
    const el = scrollerRef.current;
    if (!el) return;
    const width = el.clientWidth;
    el.scrollTo({ left: width * index, behavior: "smooth" });
  }, []);

  useEffect(() => {
    setActiveIndex(0);
    const el = scrollerRef.current;
    if (el) {
      el.scrollTo({ left: 0, behavior: "auto" });
    }
  }, [activeTab]);

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el || !el.clientWidth) return;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    if (index !== activeIndex && index >= 0 && index < effects.length) {
      setActiveIndex(index);
    }
  };

  const goPrev = () => {
    const next = Math.max(0, activeIndex - 1);
    setActiveIndex(next);
    scrollToIndex(next);
  };

  const goNext = () => {
    const next = Math.min(effects.length - 1, activeIndex + 1);
    setActiveIndex(next);
    scrollToIndex(next);
  };

  return (
    <section className="py-12 md:py-20">
      <div className="container">
        <div className="wrap">
          <div className="mx-auto mb-8 flex w-fit items-center md:mb-10">
            <div className="vertical-line gradient-vertical" />
            <h1 className="font-heading text-2xl font-extrabold text-main-dark md:text-[2.5rem]">
              {t.title}
            </h1>
          </div>

          <p className="mx-auto mb-8 max-w-xl text-center font-copy text-main-dark/70 md:mb-10">
            {t.subtitle}
          </p>

          {/* Iris count tabs */}
          <div
            role="tablist"
            aria-label={t.title}
            className="mx-auto mb-8 flex max-w-2xl overflow-x-auto border-b border-main-dark/20 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {TAB_IDS.map((tabId) => {
              const isActive = activeTab === tabId;
              return (
                <button
                  key={tabId}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tabId)}
                  className={`shrink-0 flex-1 px-2 py-3 font-heading text-xs font-bold transition-colors sm:text-sm md:text-base ${
                    isActive
                      ? "border-b-2 border-main-dark text-main-dark -mb-px"
                      : "text-main-dark/45 hover:text-main-dark/75"
                  }`}
                >
                  {t.tabs[tabId]}
                </button>
              );
            })}
          </div>

          {/* Slider */}
          <div className="relative mx-auto w-full max-w-[420px] md:max-w-[520px]">
            <div
              ref={scrollerRef}
              onScroll={handleScroll}
              className="flex aspect-square snap-x snap-mandatory overflow-x-auto scroll-smooth bg-black [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {effects.map((effect) => (
                <div
                  key={`${activeTab}-${effect.name}-${effect.src}`}
                  className="h-full w-full shrink-0 snap-center snap-always"
                >
                  <img
                    src={effect.src}
                    alt={effect.name}
                    className="h-full w-full object-contain"
                    draggable={false}
                  />
                </div>
              ))}
            </div>

            {/* Nav arrows */}
            {effects.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={activeIndex === 0}
                  aria-label={t.prev}
                  className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center bg-black/50 text-main-light transition-opacity disabled:opacity-20 md:left-3 md:h-12 md:w-12"
                >
                  <ChevronLeft
                    className="h-6 w-6"
                    color="#f3f3f3"
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={activeIndex === effects.length - 1}
                  aria-label={t.next}
                  className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center bg-black/50 text-main-light transition-opacity disabled:opacity-20 md:right-3 md:h-12 md:w-12"
                >
                  <ChevronRight
                    className="h-6 w-6"
                    color="#f3f3f3"
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                </button>
              </>
            )}
          </div>

          {/* Caption + dots */}
          <div className="mt-5 flex flex-col items-center gap-3">
            <p className="font-heading text-sm font-bold tracking-wide text-main-dark md:text-base">
              {effects[activeIndex]?.name}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {effects.map((effect, index) => (
                <button
                  key={`dot-${effect.name}-${index}`}
                  type="button"
                  aria-label={effect.name}
                  aria-current={index === activeIndex ? "true" : undefined}
                  onClick={() => {
                    setActiveIndex(index);
                    scrollToIndex(index);
                  }}
                  className={`h-2 w-2 transition-colors ${
                    index === activeIndex
                      ? "bg-main-dark"
                      : "bg-main-dark/25 hover:bg-main-dark/50"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default EffectsSection;
